import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Inject,
	OnInit,
	Output,
} from '@angular/core';
import { NgForm } from '@angular/forms';
import { OnSelectBranch } from '@components/searchable-fields/branch-search/branch-search.component';
import { onSelectInstitution } from 'src/app/shared/components/searchable-fields/lender-search-input/lender-search-input.types';
import { ICON } from '@core/util/icon';
import { Address } from '@models/address';
import { ILocalBranch } from '@models/localBranch';
import { FinancialInstitutionLender } from '@models/lender';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SnackbarService } from '@services/ui/snackbar.service';
import { LocalBranchDispatch } from '@state/business/localBranches/localBranches.dispatch';
import { LocalBranchesState } from '@state/business/localBranches/localBranches.state';
import { GlobalFinancialInstitutionsState } from '@state/global-financial-institutions/global-financial-institutions.state';
import { MatterMortgageDispatch } from '@state/matter/matter_mortgages';
import { cloneDeep } from 'lodash';
import LogRocket from 'logrocket';
import { DRAWER_DATA } from 'src/app/ui/drawer/drawer-data.token';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

@Component({
	selector: 'app-add-edit-local-branch',
	templateUrl: './add-edit-local-branch.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AddEditLocalBranchComponent implements OnInit {
	@SelectSnapshot(LocalBranchesState.get) localBranchFilter: (
		id: string,
	) => ILocalBranch;
	@SelectSnapshot(GlobalFinancialInstitutionsState.getInstitutionById)
	getInstitutionById: (id: string) => FinancialInstitutionLender;
	public localBranch: ILocalBranch = {
		id: null,
		transitNumber: '',
		institutionNumber: '',
		name: '',
		shortName: '',
		address: null,
		institutionGlobalReferenceId: null,
	};
	public ICON = ICON;
	public selectedFinancialInstitution: FinancialInstitutionLender;
	public submitButtonText = 'Create Local Branch';
	public saving = false;
	public readonlyFinancialInstitution = false;
	public mortgageId: string;

	@Output() onClose: EventEmitter<void> = new EventEmitter();
	@Output() onLocalBranchCreated: EventEmitter<ILocalBranch> =
		new EventEmitter();

	constructor(
		private localBranchDispatch: LocalBranchDispatch,
		private snackbarService: SnackbarService,
		private drawerService: DrawerService,
		private mortgageDispatch: MatterMortgageDispatch,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		@Inject(DRAWER_DATA) private data: any,
	) {}

	public ngOnInit() {
		if (this.data?.financialInstitution) {
			this.readonlyFinancialInstitution = true;
			let financialInstitution = this.data
				.financialInstitution as FinancialInstitutionLender;

			if (this.data?.financialInstitution.referenceId) {
				financialInstitution = this.getInstitutionById(
					this.data.financialInstitution.referenceId,
				);
			}

			this.selectedFinancialInstitution = financialInstitution;
			this.localBranch.shortName = financialInstitution.shortName;
			this.localBranch.name = financialInstitution.name;
			this.localBranch.institutionNumber =
				financialInstitution.institutionNumber;
			this.localBranch.institutionGlobalReferenceId = financialInstitution.id;
		}
		if (this.data?.localBranchId) {
			this.setLocalBranchById(this.data.localBranchId);
		}
		if (this.data?.mortgageId) {
			this.mortgageId = this.data.mortgageId;
		}
	}

	setLocalBranchById(localBranchId: string) {
		const localBranch = this.localBranchFilter(localBranchId);
		if (localBranch) {
			this.localBranch = cloneDeep(localBranch);
			this.submitButtonText = 'Update Local Branch';
		}
	}

	close() {
		this.drawerService.closeDrawer();
	}

	onAddressUpdate(address: Address) {
		this.localBranch.address = address;
	}

	onFinancialInstitutionSelected(event: onSelectInstitution) {
		this.selectedFinancialInstitution = event.financialInstitution;
		this.localBranch.shortName = event.financialInstitution.shortName;
		this.localBranch.name = event.financialInstitution.name;
		this.localBranch.institutionNumber =
			event.financialInstitution.institutionNumber;
		this.localBranch.institutionGlobalReferenceId =
			event.financialInstitution.id;
	}

	onSelectedBranch(event: OnSelectBranch) {
		this.localBranch.transitNumber = event.branch.transitNumber;
		this.localBranch.address = event.branch.address;
	}

	handleError(error, message?: string) {
		LogRocket.captureException(error, {
			extra: { text: message || 'Unable to save local branch' },
		});
		this.snackbarService.openSnackBar({
			text: message || 'Unable to save local branch',
			type: 'error',
		});
	}

	save(form: NgForm) {
		if (!form.valid) {
			return;
		}
		try {
			this.saving = true;
			const toBeSaved = { ...this.localBranch };
			if (this.localBranch.id) {
				this.localBranchDispatch.UpdateLocalBranch(toBeSaved);
				this.snackbarService.openSnackBar({
					text: 'Local Branch Updated',
					type: 'success',
				});
				return;
			}

			this.localBranchDispatch.Create(toBeSaved, this.mortgageId);

			this.snackbarService.openSnackBar({
				text: 'Local Branch Created',
				type: 'success',
			});
		} catch (error) {
			this.handleError(error);
		} finally {
			this.saving = false;
			this.close();
		}
	}

	onDeleteLocalBranch() {
		try {
			if (!this.localBranch.id) {
				throw new Error('Local branch ID is missing');
			}
			this.localBranchDispatch.Remove(this.localBranch.id);
			this.close();
		} catch (error) {
			this.handleError(error, 'Unable to delete local branch');
		}
	}
}
